// Planta da sala de projeto: gerada pelo número de mesas. GABARITO para as plantas das
// áreas comuns (tarefa H11), que são escritas à mão como grade de texto.
//
// As mesas ficam em ilhas frente a frente. Cada ilha (6 estações, 3 por coluna) tem:
//   linha y0      cadeira atrás da mesa de cima (agente virado para baixo, de frente para a câmera)
//   linha y0+1    mesa de cima, com a traseira do monitor (sem tela)
//   linha y0+2    divisória baixa separando as duas fileiras
//   linha y0+3    mesa de baixo, com a tela do monitor voltada para a câmera
//   linha y0+4    cadeira escura na frente da mesa de baixo (agente virado para cima, de costas)
// Entre as colunas há uma baia (divisória baixa) no tile de folga, e as divisórias só aparecem
// onde há mesa do lado. A linha y0+5 é o corredor entre ilhas (a última, com a porta, é o corredor
// de baixo da sala). Os agentes entram pela porta do corredor.

import type { Placement, RoomMap } from '../../core/world.js';

/** Colunas de mesas por ilha (cada coluna: 2 tiles de mesa, 1 de baia). */
const COLS = 3;
/** Estações por ilha: uma fileira de cima e uma de baixo. */
const ISLAND_SLOTS = COLS * 2;
/** Linhas por ilha, contando o corredor embaixo dela. */
const ISLAND_ROWS = 6;
/** Mesas no máximo por sala (as de acima de 12 não cabem). */
const MAX_DESKS = 12;

export function projectRoom(id: string, name: string, desks: number): RoomMap {
  const count = Math.max(1, Math.min(MAX_DESKS, desks));
  const islands = Math.ceil(count / ISLAND_SLOTS);
  const w = 1 + COLS * 3;
  const h = 1 + islands * ISLAND_ROWS;
  const items: Placement[] = [
    { item: 'janela', x: 1, y: 0 },
    { item: 'relogio', x: 4, y: 0 },
    { item: 'janela', x: 6, y: 0 },
  ];
  // onde há mesa: ilha/coluna/fileira (0 = de cima, 1 = de baixo)
  const taken = new Set<string>();
  for (let i = 0; i < count; i++) {
    const island = Math.floor(i / ISLAND_SLOTS);
    const slot = i % ISLAND_SLOTS;
    const col = slot % COLS;
    const top = slot < COLS;
    const x = 1 + col * 3;
    const y0 = 1 + island * ISLAND_ROWS;
    taken.add(`${island}:${col}:${top ? 0 : 1}`);
    if (top) {
      items.push({ item: 'cadeira-escritorio', x: x + 1, y: y0 }, { item: 'estacao-costas', x, y: y0 + 1 });
    } else {
      items.push({ item: 'estacao-trabalho', x, y: y0 + 3 }, { item: 'cadeira-escritorio-costas', x: x + 1, y: y0 + 4 });
    }
  }
  // baias só onde há mesa do lado: a divisória entre as fileiras fica sob cada coluna ocupada
  for (let k = 0; k < islands; k++) {
    const y0 = 1 + k * ISLAND_ROWS;
    const has = (col: number, row: number) => taken.has(`${k}:${col}:${row}`);
    for (let col = 0; col < COLS; col++) {
      const x = 1 + col * 3;
      if (has(col, 0) || has(col, 1)) items.push({ item: 'divisoria-baixa', x, y: y0 + 2 }, { item: 'divisoria-baixa', x: x + 1, y: y0 + 2 });
    }
    // entre duas mesas lado a lado (na fileira de cima e na de baixo)
    for (let col = 0; col < COLS - 1; col++) {
      const x = 3 + col * 3;
      if (has(col, 0) || has(col + 1, 0)) items.push({ item: 'divisoria-baixa', x, y: y0 + 1 });
      if (has(col, 1) || has(col + 1, 1)) items.push({ item: 'divisoria-baixa', x, y: y0 + 3 });
      if (has(col, 0) || has(col + 1, 0) || has(col, 1) || has(col + 1, 1)) items.push({ item: 'divisoria-baixa', x, y: y0 + 2 });
    }
  }
  return {
    id,
    kind: 'project',
    name,
    floor: Array.from({ length: h }, () => 'm'.repeat(w)),
    legend: { m: 'madeira' },
    items,
    doors: [{ x: Math.floor(w / 2), y: h - 1 }],
  };
}
